import {
  DocumentSidebar,
  type DocumentPeriodInfo,
  type DocumentSidebarToggleState,
} from "@/components/document-sidebar";
import {
  DsdEditorDocumentScrollArea,
  useDsdEditorDocumentBody,
} from "./document-body";
import type { DsdEditorDocumentContentModel } from "./document-view-types";
import { ReviewCellHint } from "./review-cell-hint";
import {
  type DsdReviewEvidenceCandidate,
  DsdReviewEvidenceHost,
} from "./evidence/evidence-host";
import { DsdReviewScrollToCellButton } from "./scroll-to-cell-button";
import { SidebarInset, SidebarProvider } from "@/styles/ui/sidebar";
import { type CSSProperties, type ReactNode, useMemo } from "react";

/** 문서 뷰. 본문 로직은 document-body, 레이아웃(목차·rail·패널)은 여기 */
export const DsdReviewDocumentView = ({
  model,
  sidebar,
  periodInfo,
  flaggedSections,
  documentHeader,
  panelOpen,
  panel,
  rail,
  showCellHint,
  onCellHintDismiss,
  evidenceItems,
  onScrollToSelectedCell,
  scrollTargetCellId,
  isHintTargetCell,
}: {
  model: DsdEditorDocumentContentModel;
  /** 목차 펼침·hover 미리보기 — 공용 상단 바의 토글과 같은 상태 */
  sidebar: DocumentSidebarToggleState;
  periodInfo?: DocumentPeriodInfo;
  /** 목차에 점 배지를 붙일 섹션 — 리뷰 결과가 있는 곳 */
  flaggedSections?: ReadonlySet<string>;
  documentHeader: ReactNode;
  panelOpen: boolean;
  panel: ReactNode;
  rail: ReactNode;
  /** 진입 코치마크(첫 색칠 셀에 테두리 + "셀을 클릭하면…" 칩 + 손 커서). 사용자 입력이 오면 뷰가 dismiss 를 부른다 */
  showCellHint: boolean;
  onCellHintDismiss: () => void;
  /** 선택 셀의 후보 — 선택 셀 아래 근거 창이 이 중 하나를 띄운다 (어느 것인지는 evidence-store) */
  evidenceItems: DsdReviewEvidenceCandidate[];
  /** 오른쪽 아래 "선택 셀로" 버튼 — 돌아갈 셀(scrollTargetCellId)을 지나 아래로 스크롤했을 때만 보인다 */
  onScrollToSelectedCell: () => void;
  /** 버튼이 보는 셀 = 버튼이 데려가는 셀. 선택 훅이 한 곳에서 정한다 */
  scrollTargetCellId: string | null;
  /** 진입 코치마크를 붙일 셀 — 색이 칠해진 상태 셀·후보 셀 (클릭만 되는 무색 셀 제외) */
  isHintTargetCell: (cellId: string) => boolean;
}) => {
  const body = useDsdEditorDocumentBody(model);
  const categories = useMemo(
    () =>
      body.sidebarCategories.map((category) => ({
        ...category,
        sections: category.sections.map((section) => ({
          ...section,
          hasChanges: flaggedSections?.has(section.id) ?? false,
        })),
      })),
    [body.sidebarCategories, flaggedSections],
  );

  return (
    <SidebarProvider
      open={sidebar.open}
      onOpenChange={sidebar.setOpen}
      className="h-full min-h-0"
      style={
        {
          "--sidebar-width": "192px",
          "--sidebar-width-icon": "3rem",
          minHeight: 0,
        } as CSSProperties
      }
    >
      <DocumentSidebar
        // 근거 창(z-40)이 목차 위, 스택 모달(z-50) 아래에 고정으로 놓이도록 목차를 z-30 으로 (공용 기본은 z-50)
        className="z-30"
        variant="flat"
        collapsible="offcanvas"
        showLogoAndToggle={false}
        peek={sidebar.peek}
        periodInfo={periodInfo}
        categories={categories}
        activeSection={body.activeSection}
        onSectionClick={body.scrollToSection}
      />

      <SidebarInset className="flex min-w-0 flex-col">
        <div className="flex min-h-0 flex-1">
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {documentHeader}
            <DsdEditorDocumentScrollArea
              body={body}
              model={model}
              // 흰 프레임은 표 모서리와 같은 6px
              contentClassName="w-full"
              frameClassName="rounded-[6px]"
            />
            <DsdReviewScrollToCellButton
              scrollContainerRef={body.scrollContainerRef}
              cellId={scrollTargetCellId}
              onClick={onScrollToSelectedCell}
              className="absolute bottom-8 right-10"
            />
          </div>
          {panelOpen && (
            // 오른쪽 패널 안 클릭은 근거 창을 닫지 않는다 (카드 호버·근거 확인·검토 원형·토글을 창과 나란히 쓴다)
            <aside
              data-keep-evidence
              className="relative hidden h-full min-h-0 w-[410px] shrink-0 overflow-hidden bg-background-base shadow-panel-left md:flex"
            >
              <div className="h-full min-h-0 w-full shrink-0">{panel}</div>
            </aside>
          )}
          {rail}
        </div>
      </SidebarInset>
      <DsdReviewEvidenceHost
        selectedCellId={model.selection?.selectedCellId ?? null}
        items={evidenceItems}
        scrollContainerRef={body.scrollContainerRef}
        itemsBySection={body.itemsBySection}
        matchedParagraphSegments={
          model.noteReferenceReview?.matchedParagraphSegments
        }
      />
      {showCellHint && (
        <ReviewCellHint
          scrollContainerRef={body.scrollContainerRef}
          onDismiss={onCellHintDismiss}
          // 색이 칠해진 첫 셀(또는 후보 셀)에 붙인다 — 클릭만 되는 무색 셀을 잡으면 "주석 대조가 제한됩니다" 만 보게 된다
          isTargetCell={isHintTargetCell}
        />
      )}
    </SidebarProvider>
  );
};
